import * as d3 from 'd3';
import { geoMercator, geoPath } from 'd3-geo';
import worldGeoJson from './data/world.json';

/**
 * Factory that returns an Alpine component object for an author-location map.
 * buildUrl receives the same arguments passed to init() and must return the
 * API URL string to fetch { nodes, edges } from.
 */
function authorMapComponent(buildUrl) {
    return () => ({
        mapContainer: null,
        svg: null,
        g: null,
        zoom: null,
        projection: null,
        path: null,
        tooltip: null,
        currentScale: 1,
        mapData: { nodes: [], edges: [] },
        noMapData: false,

        async init() {
            await this.$nextTick();
            await this.loadMapData(buildUrl(this.$el.dataset));

            if (this.mapData.nodes.length >= 2) {
                this.initMap();
            } else {
                this.noMapData = true;
            }
        },

        clearMap() {
            const svgEl = this.$refs.mapSvg;
            d3.select(svgEl).on('.zoom', null);
            d3.select(svgEl).selectAll('*').remove();
            if (this.tooltip) {
                this.tooltip.remove();
                this.tooltip = null;
            }
            this.svg = null;
            this.g = null;
            this.zoom = null;
        },

        async reloadMap(url) {
            this.noMapData = false;
            this.clearMap();
            await this.loadMapData(url);
            if (this.mapData.nodes.length >= 2) {
                this.initMap();
            } else {
                this.noMapData = true;
            }
        },

        async loadMapData(url) {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
                this.mapData = await response.json();
            } catch (error) {
                console.error('Failed to load author map data:', error);
                this.noMapData = true;
            }
        },

        initMap() {
            this.mapContainer = this.$refs.mapContainer;
            const width = this.mapContainer.clientWidth;
            const height = this.mapContainer.clientHeight;

            this.svg = d3.select(this.$refs.mapSvg)
                .attr('width', '100%')
                .attr('height', '100%')
                .attr('viewBox', [0, 0, width, height]);

            this.g = this.svg.append('g');

            this.projection = geoMercator()
                .center([19, 45])
                .scale(width / 1.5)
                .translate([width / 2, height / 2]);

            this.path = geoPath().projection(this.projection);

            const eurasianCountries = worldGeoJson.features.filter(d => {
                const continent = this.getContinentByCountry(d.properties.name);
                return continent === 'Europe' || continent === 'Asia';
            });

            this.g.selectAll('path.country')
                .data(eurasianCountries)
                .join('path')
                .attr('d', this.path)
                .attr('class', d => {
                    const continent = this.getContinentByCountry(d.properties.name);
                    return continent === 'Europe' ? 'country europe' : 'country asia hidden';
                });

            this.tooltip = d3.select(this.mapContainer)
                .append('div')
                .attr('class', 'map-tooltip')
                .style('opacity', 0);

            const nodeById = Object.fromEntries(this.mapData.nodes.map(n => [n.id, n]));

            const ellisBlue = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-ellis-blue').trim() || '#6a9bdd';
            const ellisOrange = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-ellis-orange').trim() || '#e38e48';

            // Connection arcs as quadratic bezier curves arching upward
            this.g.selectAll('path.author-arc')
                .data(this.mapData.edges)
                .join('path')
                .attr('class', 'author-arc')
                .attr('d', d => {
                    const source = nodeById[d.source];
                    const target = nodeById[d.target];
                    if (!source || !target) return null;
                    const [sx, sy] = this.projection([source.longitude, source.latitude]);
                    const [tx, ty] = this.projection([target.longitude, target.latitude]);
                    const mx = (sx + tx) / 2;
                    const my = (sy + ty) / 2 - Math.abs(tx - sx) * 0.25;
                    return `M ${sx} ${sy} Q ${mx} ${my} ${tx} ${ty}`;
                })
                .attr('fill', 'none')
                .attr('stroke', ellisBlue)
                .attr('stroke-width', 1.5)
                .attr('stroke-opacity', 0.5)
                .attr('stroke-dasharray', '4 3');

            // Author location markers
            this.g.selectAll('circle.author-marker')
                .data(this.mapData.nodes)
                .join('circle')
                .attr('class', 'author-marker')
                .attr('cx', d => this.projection([d.longitude, d.latitude])[0])
                .attr('cy', d => this.projection([d.longitude, d.latitude])[1])
                .attr('r', 5)
                .attr('fill', ellisOrange)
                .attr('stroke', 'white')
                .attr('stroke-width', 1.5)
                .on('mouseover', (event, d) => {
                    const containerRect = this.mapContainer.getBoundingClientRect();
                    const cols = d.authors.length > 10 ? Math.min(Math.ceil(d.authors.length / 10), 3) : 1;
                    const authorList = d.authors.length
                        ? `<ul class="map-tooltip-authors" style="columns:${cols}">${d.authors.map(a => `<li>${a}</li>`).join('')}</ul>`
                        : '';

                    // Set content and class first so the browser lays out the final width
                    this.tooltip
                        .interrupt()
                        .classed('map-tooltip--multi', cols > 1)
                        .html(`<strong>${d.name}</strong>${authorList}`);

                    // offsetWidth/offsetHeight force a synchronous reflow at final dimensions
                    const tipWidth  = this.tooltip.node().offsetWidth;
                    const tipHeight = this.tooltip.node().offsetHeight;
                    const rawX = event.clientX - containerRect.left;
                    const rawY = event.clientY - containerRect.top;
                    const x = rawX + tipWidth + 20 > containerRect.width ? rawX - tipWidth - 10 : rawX + 10;
                    const y = Math.max(0, rawY - tipHeight / 2);

                    // Position is applied before the opacity transition begins
                    this.tooltip
                        .style('left', `${x}px`)
                        .style('top', `${y}px`)
                        .transition().duration(200).style('opacity', 0.9);

                    d3.select(event.currentTarget).attr('r', 8 / this.currentScale);
                })
                .on('mouseout', (event) => {
                    this.tooltip.classed('map-tooltip--multi', false).transition().duration(500).style('opacity', 0);
                    d3.select(event.currentTarget).attr('r', 5 / this.currentScale);
                });

            this.zoom = d3.zoom()
                .scaleExtent([0.5, 8])
                .on('zoom', (event) => {
                    this.g.attr('transform', event.transform);
                    this.currentScale = event.transform.k;
                    this.g.selectAll('.author-marker').attr('r', 5 / event.transform.k);
                });

            this.svg.call(this.zoom);
            this.fitView();
        },

        fitView() {
            if (this.mapData.nodes.length === 0) return;

            const lngs = this.mapData.nodes.map(n => n.longitude);
            const lats = this.mapData.nodes.map(n => n.latitude);
            const pad = 8;

            const [x0, y0] = this.projection([Math.min(...lngs) - pad, Math.max(...lats) + pad]);
            const [x1, y1] = this.projection([Math.max(...lngs) + pad, Math.min(...lats) - pad]);

            const width = this.mapContainer.clientWidth;
            const height = this.mapContainer.clientHeight;

            const scale = Math.min(
                0.85 * width / Math.abs(x1 - x0),
                0.85 * height / Math.abs(y1 - y0),
                8
            );

            const translateX = width / 2 - scale * (x0 + x1) / 2;
            const translateY = height / 2 - scale * (y0 + y1) / 2;

            this.svg.transition().duration(600).call(
                this.zoom.transform,
                d3.zoomIdentity.translate(translateX, translateY).scale(scale)
            );
        },

        zoomIn() { this.handleZoom(1.2); },
        zoomOut() { this.handleZoom(0.8); },

        handleZoom(factor) {
            const transform = d3.zoomTransform(this.svg.node());
            const newScale = transform.k * factor;
            if (newScale >= 0.5 && newScale <= 8) {
                this.svg.transition().duration(300).call(this.zoom.scaleTo, newScale);
            }
        },

        getContinentByCountry(countryName) {
            const europeanCountries = [
                'France', 'Germany', 'Spain', 'Italy', 'England',
                'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Denmark',
                'Sweden', 'Norway', 'Finland', 'Poland', 'Czech Republic',
                'Slovakia', 'Hungary', 'Romania', 'Bulgaria', 'Greece',
                'Portugal', 'Ireland', 'Estonia', 'Latvia', 'Iceland',
                'Lithuania', 'Belarus', 'Ukraine', 'Moldova', 'Croatia',
                'Slovenia', 'Republic of Serbia', 'Bosnia and Herzegovina', 'Albania',
                'Macedonia', 'Montenegro', 'Luxembourg', 'Malta',
                'Israel', 'Kosovo', 'Turkey',
            ];
            return europeanCountries.includes(countryName) ? 'Europe' : 'Asia';
        },
    });
}

document.addEventListener('alpine:init', () => {
    // Per-publication map on the publication detail page
    Alpine.data('publicationAuthorMap', authorMapComponent(
        ({ slug }) => `/api/publication/${slug}/authors-map`
    ));

    // Year-range map for the Bard block component
    const buildBlockUrl = ({ yearFrom, yearTo }) => {
        const params = new URLSearchParams({ year_from: yearFrom || 2018 });
        if (yearTo) params.set('year_to', yearTo);
        return `/api/map/author-locations?${params}`;
    };

    Alpine.data('publicationAuthorsMapBlock', () => ({
        ...authorMapComponent(buildBlockUrl)(),

        selectedYear: '',
        isOpen: false,
        openedWithKeyboard: false,
        openUpwards: false,

        get years() {
            const from = parseInt(this.$el.dataset.yearFrom) || 2018;
            const toRaw = this.$el.dataset.yearTo;
            // When year_to is unset, cap at the last completed year — the
            // in-progress current year has no publications yet.
            const to = toRaw ? parseInt(toRaw) : new Date().getFullYear() - 1;
            const arr = [];
            for (let y = to; y >= from; y--) arr.push(String(y));
            return arr;
        },

        get label() {
            return this.selectedYear || 'All years';
        },

        async init() {
            await this.$nextTick();
            this.selectedYear = this.years[0] || '';
            const url = this.selectedYear
                ? buildBlockUrl({ ...this.$el.dataset, yearTo: this.selectedYear })
                : buildBlockUrl(this.$el.dataset);
            await this.loadMapData(url);
            if (this.mapData.nodes.length >= 2) {
                this.initMap();
            } else {
                this.noMapData = true;
            }
        },

        async selectYear(year) {
            this.selectedYear = year;
            this.isOpen = false;
            this.openedWithKeyboard = false;
            const url = year
                ? buildBlockUrl({ ...this.$el.dataset, yearTo: year })
                : buildBlockUrl(this.$el.dataset);
            await this.reloadMap(url);
        },

        checkPosition() {
            this.$nextTick(() => {
                this.openUpwards = (window.innerHeight - this.$refs.dropdownButton.getBoundingClientRect().bottom) < this.$refs.dropdown.getBoundingClientRect().height;
            });
        },
    }));
});
